import Box from '@mui/material/Box';
import { Trans, useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';

import {
    ExtraCategoriesDocument,
    IContentTypeValue,
    ICertificateEditFragment,
    IFileFragment,
    ITemplate,
} from 'graphql/types';
import { getExtraCategoryInitialFormValues } from 'organisation/utils/extraCategory';
import { getCertificateFormValidator } from 'certificate/forms/validators/certificateForm';
import { SwitchField, TextField } from 'common/forms/Field';
import { Divider } from 'common/components/Divider';
import { FormSection } from 'common/components/FormSection';
import { TemplateSection } from 'certificate/forms/CertificateForm/TemplateSection';
import { ContentExpander } from 'common/components/ContentExpander';
import { FormFooter } from 'common/forms/components/FormFooter';
import { SettingsSection } from 'certificate/forms/CertificateForm/SettingsSection';
import { NotificationSection } from 'certificate/forms/CertificateForm/NotificationSection';
import { CertificatePreviewListItem } from 'certificate/components';
import { Link } from 'common/components/Link';
import { InformationSection } from 'certificate/forms/CertificateForm/InformationSection';
import {
    EScoringSystemOption,
    TCertificateFormValues,
} from 'certificate/types';
import { ExtraFieldsSection } from 'certificate/forms/CertificateForm/ExtraFieldsSection';
import { ExtraCategoriesSection } from 'certificate/forms/CertificateForm/ExtraCategoriesSection';

interface IProps {
    certificate?: ICertificateEditFragment;
    onCreate: (
        values: TCertificateFormValues,
        isExternal?: boolean
    ) => void | Promise<void>;
    onClose?(): void;
    onUpdate?: SubmitHandler<TCertificateFormValues>;
    onDelete?(): void | Promise<void>;
}

export const CertificateForm = ({
    certificate,
    onClose,
    onCreate,
    onUpdate,
    onDelete,
}: IProps) => {
    const [translate] = useTranslation();
    const [moreSettingsExpanded, setMoreSettingsExpanded] = useState(false);
    const [uploadedSignatureFile, setUploadedSignatureFile] =
        useState<IFileFragment | null>(null);
    const [uploadedCustomTemplateFile, setUploadedCustomTemplateFile] =
        useState<IFileFragment | null>(null);

    const handleSignatureFileUpload = useCallback(
        (file: IFileFragment | null) => {
            setUploadedSignatureFile(file);
        },
        []
    );

    const handleCustomTemplateFileUpload = useCallback(
        (file: IFileFragment | null) => {
            setUploadedCustomTemplateFile(file);
        },
        []
    );

    const isEdit = !!certificate;

    const { data: extraCategoriesData } = useQuery(ExtraCategoriesDocument, {
        variables: { contentType: IContentTypeValue.Certificate },
    });

    const defaultValues = useMemo<TCertificateFormValues>(
        () => ({
            template: certificate?.template || ITemplate.AcademyModern,
            name: certificate?.name || '',
            title: certificate?.title || '',
            subtitle: certificate?.subtitle || '',
            text: certificate?.text || '',
            primaryColor: certificate?.primaryColor?.replace(/^#/, '') || '',
            secondaryColor:
                certificate?.secondaryColor?.replace(/^#/, '') || '',
            customTemplateId: certificate?.customTemplate?.id || undefined,
            showCourses: certificate?.showCourses ?? false,
            showCourseScore: certificate?.showCourseScore ?? false,
            showCourseTimeSpent: certificate?.showCourseTimeSpent ?? false,
            pageTwoText: certificate?.pageTwoText || '',
            expiry: certificate?.expiry || null,
            expiryScale: certificate?.expiryScale || undefined,
            sendAchievedMail: certificate?.sendAchievedMail ?? false,
            sendNotification: certificate?.sendNotification ?? false,
            notificationOffset: certificate?.notificationOffset || undefined,
            notificationOffsetScale:
                certificate?.notificationOffsetScale || undefined,
            notificationTargets: certificate?.notificationTargets || [],
            scoring: {
                score: certificate?.scoring?.[0]?.score ?? undefined,
                scoringSystemId:
                    certificate?.scoring?.[0]?.scoringSystem?.id ??
                    EScoringSystemOption.NoScoringSystem,
            },
            extraFields: certificate?.extraFields || [],
            extraCategories: getExtraCategoryInitialFormValues(
                extraCategoriesData?.extraCategories,
                certificate?.extraCategoryValues
            ),
            reference: certificate?.ref || undefined,
            signatureFileId: certificate?.signatureFile?.id || undefined,
        }),
        [certificate, extraCategoriesData?.extraCategories]
    );

    const formMethods = useForm<TCertificateFormValues>({
        defaultValues,
        resolver: zodResolver(getCertificateFormValidator()),
    });

    const { control, handleSubmit, reset } = formMethods;

    // Reset form when certificate data changes
    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    const handleFormSubmit: SubmitHandler<TCertificateFormValues> =
        isEdit && onUpdate ? onUpdate : (values) => onCreate(values);

    return (
        <FormProvider {...formMethods}>
            <CertificatePreviewListItem
                control={control}
                customTemplateFile={
                    uploadedCustomTemplateFile ?? certificate?.customTemplate
                }
                signatureFile={
                    uploadedSignatureFile ?? certificate?.signatureFile
                }
                thumbnail={certificate?.thumbnail}
            />

            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <Divider sx={{ my: 2 }} />

                <TextField
                    control={control}
                    label={translate('name')}
                    name="name"
                />

                <Divider sx={{ mt: 3 }} />

                <TemplateSection
                    control={control}
                    customTemplateFile={
                        uploadedCustomTemplateFile ??
                        certificate?.customTemplate
                    }
                    onCustomTemplateFileUpload={handleCustomTemplateFileUpload}
                />

                <InformationSection
                    control={control}
                    signatureFile={certificate?.signatureFile}
                    onSignatureFileUpload={handleSignatureFileUpload}
                />

                <SettingsSection control={control} />

                <ContentExpander
                    renderExpanded
                    expand={moreSettingsExpanded}
                    title={translate('moreSettings')}
                    onClick={() =>
                        setMoreSettingsExpanded(!moreSettingsExpanded)
                    }
                >
                    <FormSection
                        renderExpanded
                        description={
                            <Box>
                                {translate(
                                    'certificateForm.courseModulesDescription'
                                )}
                            </Box>
                        }
                        title={translate('certificateForm.courseModulesTitle')}
                    >
                        <SwitchField
                            control={control}
                            formControlProps={{ margin: 'none' }}
                            label={translate('certificateForm.showCourses')}
                            name="showCourses"
                        />

                        <SwitchField
                            control={control}
                            formControlProps={{ margin: 'none' }}
                            label={translate('certificateForm.showCourseScore')}
                            name="showCourseScore"
                        />

                        <SwitchField
                            control={control}
                            formControlProps={{ margin: 'none' }}
                            label={translate(
                                'certificateForm.showCourseTimeSpent'
                            )}
                            name="showCourseTimeSpent"
                        />

                        <TextField
                            multiline
                            control={control}
                            label={translate('certificateForm.guidingText')}
                            name="pageTwoText"
                        />
                    </FormSection>

                    <NotificationSection control={control} />

                    <FormSection
                        description={
                            <Trans
                                components={{
                                    a: (
                                        <Link
                                            rel="noreferrer"
                                            underline="always"
                                        />
                                    ),
                                }}
                                i18nKey="certificateForm.connectionsDescription"
                            />
                        }
                        title={translate('certificateForm.connectionsTitle')}
                    >
                        <TextField
                            control={control}
                            label={translate('certificateForm.reference')}
                            name="reference"
                        />
                    </FormSection>

                    <ExtraFieldsSection control={control} />

                    <ExtraCategoriesSection control={control} />
                </ContentExpander>

                <FormFooter
                    canDelete={!!onDelete}
                    deleteDialogText={translate(
                        'certificateForm.deleteDialogText'
                    )}
                    deleteDialogTitle={translate(
                        'certificateForm.deleteDialogTitle'
                    )}
                    onClose={onClose}
                    onDelete={onDelete}
                />
            </form>
        </FormProvider>
    );
};
